/**
 * A canonical digest of the verification producer map.
 *
 * The resolver pins this digest into the immutable execution definition so that a later edit to the
 * producer plan cannot silently re-interpret an in-flight attempt's proof: the ingested result
 * either matches the digest the attempt was dispatched under, or it belongs to a different
 * plan and needs a fresh snapshot.
 *
 * SHA-256 is implemented here rather than imported from `node:crypto` because this module is
 * browser-safe: the dashboard compiles the config package through its alias, and a `node:`
 * import would end the Vite build (see AGENTS.md). The input is a few hundred bytes of JSON,
 * so the cost of a hand-rolled hash is irrelevant and `crypto.subtle` being async is not worth
 * spreading a promise through every caller.
 */
import type { VerificationProducer, VerificationSection } from './types.ts';

const K = [
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
] as const;

function rotr(value: number, bits: number): number {
  return ((value >>> bits) | (value << (32 - bits))) >>> 0;
}

/** SHA-256 of a UTF-8 string, lowercase hex. */
export function sha256Hex(text: string): string {
  return sha256BytesHex(new TextEncoder().encode(text));
}

/**
 * SHA-256 of raw bytes, lowercase hex.
 *
 * Verification proof is hashed as bytes: a PNG routed through `sha256Hex` would be hashed as
 * the UTF-8 encoding of its own mojibake, so two different images that both decode to
 * replacement characters in the same places would digest identically.
 */
export function sha256BytesHex(bytes: Uint8Array): string {
  const bitLength = bytes.length * 8;
  const blocks = Math.ceil((bytes.length + 9) / 64);
  const padded = new Uint8Array(blocks * 64);
  padded.set(bytes);
  padded[bytes.length] = 0x80;
  // Length is written as a 64-bit big-endian count of bits; the high word is derived by
  // division rather than a shift because a shift would wrap at 32 bits.
  const view = new DataView(padded.buffer);
  view.setUint32(padded.length - 8, Math.floor(bitLength / 0x100000000), false);
  view.setUint32(padded.length - 4, bitLength >>> 0, false);

  let h0 = 0x6a09e667;
  let h1 = 0xbb67ae85;
  let h2 = 0x3c6ef372;
  let h3 = 0xa54ff53a;
  let h4 = 0x510e527f;
  let h5 = 0x9b05688c;
  let h6 = 0x1f83d9ab;
  let h7 = 0x5be0cd19;

  const w = new Uint32Array(64);
  for (let block = 0; block < blocks; block++) {
    const offset = block * 64;
    for (let i = 0; i < 16; i++) w[i] = view.getUint32(offset + i * 4, false);
    for (let i = 16; i < 64; i++) {
      const a = w[i - 15] as number;
      const b = w[i - 2] as number;
      const s0 = (rotr(a, 7) ^ rotr(a, 18) ^ (a >>> 3)) >>> 0;
      const s1 = (rotr(b, 17) ^ rotr(b, 19) ^ (b >>> 10)) >>> 0;
      w[i] = ((w[i - 16] as number) + s0 + (w[i - 7] as number) + s1) >>> 0;
    }
    let a = h0;
    let b = h1;
    let c = h2;
    let d = h3;
    let e = h4;
    let f = h5;
    let g = h6;
    let h = h7;
    for (let i = 0; i < 64; i++) {
      const s1 = (rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25)) >>> 0;
      const ch = ((e & f) ^ (~e & g)) >>> 0;
      const temp1 = (h + s1 + ch + (K[i] as number) + (w[i] as number)) >>> 0;
      const s0 = (rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22)) >>> 0;
      const maj = ((a & b) ^ (a & c) ^ (b & c)) >>> 0;
      const temp2 = (s0 + maj) >>> 0;
      h = g;
      g = f;
      f = e;
      e = (d + temp1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (temp1 + temp2) >>> 0;
    }
    h0 = (h0 + a) >>> 0;
    h1 = (h1 + b) >>> 0;
    h2 = (h2 + c) >>> 0;
    h3 = (h3 + d) >>> 0;
    h4 = (h4 + e) >>> 0;
    h5 = (h5 + f) >>> 0;
    h6 = (h6 + g) >>> 0;
    h7 = (h7 + h) >>> 0;
  }
  return [h0, h1, h2, h3, h4, h5, h6, h7].map((value) => value.toString(16).padStart(8, '0')).join('');
}

/**
 * The canonical JSON a digest is taken over: producers sorted by name, `provides` and
 * `scenarios` sorted, and the producer fields in a fixed order. Key order in the stored document is
 * incidental; two configurations that declare the same plan must digest identically.
 *
 * `scenarios` is written only when the producer declares it, so every plan that predates
 * partitions keeps the digest it already has: a pin written by 0.20 still validates its own
 * producers after this change. Moving one scenario from one producer to another changes no
 * other field, and it is a different plan -- so it has to change the digest, or an attempt
 * dispatched under the old division would accept proof answering the new one.
 */
export function canonicalProducerJson(section: VerificationSection): string {
  const names = Object.keys(section.producers).sort();
  const canonical = names.map((name) => {
    const producer = section.producers[name] as VerificationProducer;
    return {
      name,
      job: producer.job,
      provides: [...producer.provides].sort(),
      ...(producer.scenarios ? { scenarios: [...producer.scenarios].sort() } : {}),
      result: producer.result,
    };
  });
  return JSON.stringify({ producers: canonical });
}

/** Lowercase hex SHA-256 of `canonicalProducerJson`. Pinned with the attempt. */
export function producerConfigDigest(section: VerificationSection): string {
  return sha256Hex(canonicalProducerJson(section));
}
